/* eslint-disable @typescript-eslint/no-use-before-define -- the dialog is
   declared first for readability; `ImageToVideoPanel` below it is a
   `function` declaration and therefore hoisted at runtime. Everything else —
   the other flows, leaf components, types, constants and prompts — lives in
   ./ai-action. */
import { useEffect, useMemo, useRef, useState } from "react"
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  BoltIcon,
  FilmIcon,
  GlobeAltIcon,
  PaintBrushIcon,
  PhotoIcon,
  RectangleStackIcon,
  SparklesIcon,
  ViewColumnsIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useDroppable, useDndMonitor, type DragEndEvent } from "@dnd-kit/core"
import { toast } from "react-toastify"
import { DRAWER_ITEM_PREFIX } from "./SelectedAssetsDrawer"
import { useAdForgeGetBrandKitQuery } from "~/ui-rtk/api/adForgeApi"
import { ImageCategory, isAvifImage, type ScrapedImage } from "../types"
import BrandOverlayPanel from "./overlay/BrandOverlayPanel"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import {
  buildBrandContext,
  buildMotionProductContext,
  buildProductContextDigest,
} from "../productContextPrompt"
import mediaRef from "./ai-action/media-ref"
import useComposePipeline from "./ai-action/hooks/useComposePipeline"
import useMotionPipeline from "./ai-action/hooks/useMotionPipeline"
import useProductPage from "./ai-action/hooks/useProductPage"
import CarouselCardsPanel from "./ai-action/CarouselCardsPanel"
import MultiAssetFlow from "./ai-action/MultiAssetFlow"
import PendingResultCard from "./ai-action/PendingResultCard"
import SourceDropZone from "./ai-action/SourceDropZone"
import {
  AD_REF_DROP_ID,
  CATEGORY_TO_ROLE,
  EFFECT_OPTIONS,
  FRAME_RATIOS,
  HERO_DROP_ID,
  MAX_MOTION_REFERENCES,
  REFERENCE_MODE_DURATION,
  REF_ROLE_LABELS,
  VEO_RATIOS,
} from "./ai-action/constants"
import {
  CATEGORY_PROMPT_LINES,
  CREATIVE_DIRECTOR_PROMPT,
  ENHANCE_PROMPT,
  HERO_ROLE_LINE,
  LUCKY_MOTION_PROMPT,
  ROLE_PROMPT_LINES,
  SUPPORTING_ROLE_LINE,
  buildImageManifest,
  buildMotionManifest,
  carouselFirstCardSuffix,
  describeRef,
} from "./ai-action/prompts"
import type {
  AiAction,
  AiUploadOptions,
  BackgroundMode,
  CarouselCardCount,
  FrameRatio,
  PanelProps,
  RefImage,
  RefRole,
  VeoDuration,
  VeoRatio,
  VeoResolution,
} from "./ai-action/types"

// The AI-action vocabulary (types, constants, prompts) and the leaf
// components live in ./ai-action. Re-exported here because AdForgePage and
// BrandOverlayPanel have always imported them from this module.
export type { AiAction, AiUploadOptions }
export { ENHANCE_PROMPT }

// ── Action catalog ────────────────────────────────────────────────────────

interface ActionConfig {
  key: AiAction
  label: string
  icon: typeof FilmIcon
  /** Actions that transform a single image need an image source before
   *  their panel can render — without one they show a drop zone instead.
   *  The slideshow builds its own timeline from drawer drops (images AND
   *  videos) so it never needs a pre-selected source. */
  needsImageSource?: boolean
  /** Brand overlay works on a single source too, but takes videos as well
   *  as images (the overlay canvas sits on either). */
  acceptsVideoSource?: boolean
}

const ACTIONS: ActionConfig[] = [
  {
    key: "image-to-image",
    label: "Image → Image",
    icon: PhotoIcon,
    needsImageSource: true,
  },
  {
    key: "image-to-carousel",
    label: "Image → Carousel",
    icon: ViewColumnsIcon,
    needsImageSource: true,
  },
  {
    key: "image-to-video",
    label: "Image → Video",
    icon: FilmIcon,
    needsImageSource: true,
  },
  {
    key: "slideshow",
    label: "Slideshow",
    icon: RectangleStackIcon,
  },
  {
    key: "brand-overlay",
    label: "Brand overlay",
    icon: PaintBrushIcon,
    // No needsImageSource: the flow opens on the layout library/builder;
    // media is picked later (Apply-to strip or ✨) and can be a video.
    acceptsVideoSource: true,
  },
]

// ── Panel props ───────────────────────────────────────────────────────────

interface AiActionPanelProps {
  /** Source asset (the one the user clicked Sparkles on). The panel is
   *  always visible — when no source is set, the single-source actions show
   *  a drop zone instead of their form. */
  asset: ScrapedImage | null
  /** Called when the user drops a drawer asset onto the source drop zone.
   *  Parent owns the source-asset state (same state the ✨ button sets). */
  onSelectAsset?: (asset: ScrapedImage) => void
  /** Assets currently staged in the ad card slots. Opening a flow adopts
   *  them automatically — Image → Video picks the first usable image as its
   *  source, the slideshow pre-fills its timeline with all of them. */
  stagedAssets?: ScrapedImage[]
  /** The full picked-asset pool (Selected Assets drawer contents). Offered
   *  as click-to-pick source candidates — the card grid with its ✨ buttons
   *  is hidden while a flow is open, so the drop zone must be
   *  self-sufficient. */
  poolAssets?: ScrapedImage[]
  /** Clears the current source asset (the panel itself stays visible). */
  onClose: () => void
  /** Fired whenever the selected flow changes (null = back on the button
   *  row). The page uses this to hide the asset section while a flow is
   *  open so the flow UI takes its place. */
  onActionChange?: (action: AiAction | null) => void
  initialAction?: AiAction
  /** External deep-link into a specific flow (e.g. the card's brush button
   *  opens Brand overlay). Bump `token` to re-trigger the same action. */
  actionRequest?: { action: AiAction; token: number } | null
  /** Called when a flow produces a downloadable result. Parent uploads it
   *  via /assets/upload and stages it in the picked pool. The optional
   *  `uploadOptions` carry source-type, parent linkage and metadata so the
   *  upload lands as an AI variant of the source asset. */
  onGenerated: (
    file: File,
    uploadOptions?: AiUploadOptions,
  ) => Promise<void> | void
  /** Flows that persist the asset server-side (e.g. motion video, which is
   *  fetched + uploaded by the backend so we don't haul base64 across the
   *  wire) hand the already-saved asset back here. Parent just stages it. */
  onAssetReady?: (asset: ScrapedImage) => Promise<void> | void
  /** "Use as carousel ad" on generated carousel cards — the page switches
   *  the ad template to carousel and fills the slots with these assets in
   *  order. */
  onUseAsCarouselAd?: (assets: ScrapedImage[]) => void
}

// ── Inline panel — always visible above the carousel/single slots ────────

export default function AiActionDialog({
  asset,
  onSelectAsset,
  stagedAssets,
  poolAssets,
  onClose,
  onActionChange,
  initialAction = "image-to-video",
  actionRequest,
  onGenerated,
  onAssetReady,
  onUseAsCarouselAd,
}: AiActionPanelProps) {
  const isVideoSource = asset?.type === "video"
  // Video sources only make sense in the slideshow (the single-source
  // actions transform an image); clicking ✨ on a video card lands there.
  const defaultAction: AiAction = isVideoSource ? "slideshow" : initialAction
  // Selected action. null shows the action chooser (the default resting
  // state of the always-visible panel); picking an action switches the body
  // to that flow's UI, with a back button to return to the chooser.
  const [action, setAction] = useState<AiAction | null>(null)

  // Assets to seed the slideshow with instead of the staged slots — set when
  // the carousel flow hands its generated cards over ("Make slideshow").
  const [slideshowSeed, setSlideshowSeed] = useState<ScrapedImage[] | null>(
    null,
  )
  useEffect(() => {
    if (action !== "slideshow") setSlideshowSeed(null)
  }, [action])

  // Jump straight into the right flow whenever a source asset is picked
  // (✨ button or drop zone) — image sources land on the deep-link action,
  // video sources land on the slideshow. If a flow is ALREADY open, keep it:
  // picking a new source there means "swap the product", not "switch flows"
  // (unless the new source is a video, which single-image flows can't take —
  // the slideshow and the brand overlay both accept videos).
  useEffect(() => {
    if (!asset) return
    setAction(prev => {
      if (prev !== null) {
        const prevTakesVideo =
          prev === "slideshow" ||
          ACTIONS.find(a => a.key === prev)?.acceptsVideoSource
        return isVideoSource && !prevTakesVideo ? "slideshow" : prev
      }
      return defaultAction
    })
  }, [asset?.id, defaultAction])

  // External deep link (card brush button → Brand overlay). Declared AFTER
  // the source-pick effect above so it wins when both fire in one commit
  // (e.g. picking a video card would otherwise route to the slideshow).
  useEffect(() => {
    if (actionRequest) setAction(actionRequest.action)
  }, [actionRequest?.token])

  // Let the page react to the flow opening/closing (it swaps the asset
  // section out for the flow UI). Effect-based so every setAction path
  // (buttons, back, flow-done, source-pick deep link) is covered.
  useEffect(() => {
    onActionChange?.(action)
  }, [action])

  // When the user picks a source via the ✨ button down in the card grid,
  // bring the panel back into view.
  const sectionRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (asset) {
      sectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
    }
  }, [asset?.id])

  const hasImageSource = !!asset && !isVideoSource

  // Click-to-pick source candidates for the drop zone: staged slot images
  // first, then the whole drawer pool. The card grid (and its ✨ buttons) is
  // hidden while a flow is open, so the drop zone must offer the assets
  // itself — dragging is not the only way in.
  const sourceCandidates = useMemo(() => {
    const seen = new Set<string>()
    const out: ScrapedImage[] = []
    for (const a of [...(stagedAssets ?? []), ...(poolAssets ?? [])]) {
      if (a.type === "video" || isAvifImage(a) || seen.has(a.id)) continue
      seen.add(a.id)
      out.push(a)
    }
    return out
  }, [stagedAssets, poolAssets])

  // The brand overlay draws ON the media rather than regenerating it, so its
  // candidate list keeps videos (and AVIF — the browser canvas decodes it
  // fine; only the sharp-based image pipeline can't).
  const overlayCandidates = useMemo(() => {
    const seen = new Set<string>()
    const out: ScrapedImage[] = []
    for (const a of [...(stagedAssets ?? []), ...(poolAssets ?? [])]) {
      if (seen.has(a.id)) continue
      seen.add(a.id)
      out.push(a)
    }
    return out
  }, [stagedAssets, poolAssets])

  // Flows call this on Cancel/Done/success — clear the source selection and
  // land back on the action chooser (the panel itself never goes away).
  const handleFlowDone = () => {
    setAction(null)
    onClose()
  }

  return (
    <section ref={sectionRef}>
      {action === null ? (
        /* Resting state — one compact row of action buttons, no chrome. */
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white mr-1">
            <SparklesIcon className="w-4 h-4 text-brand" />
            Create with AI
          </span>
          {ACTIONS.map(cfg => {
            const Icon = cfg.icon
            return (
              <button
                key={cfg.key}
                onClick={() => {
                  // Adopt the already-staged image as the source so the
                  // flow opens ready to generate instead of asking for a
                  // drop. AVIF can't go through the image pipeline, so it
                  // never auto-qualifies (except for the overlay, which
                  // renders in-browser and takes videos too).
                  if (cfg.needsImageSource && !asset) {
                    const staged = stagedAssets?.find(a =>
                      cfg.acceptsVideoSource
                        ? true
                        : a.type !== "video" && !isAvifImage(a),
                    )
                    if (staged) onSelectAsset?.(staged)
                  }
                  setAction(cfg.key)
                }}
                className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white hover:border-brand/50 gap-1.5"
              >
                <Icon className="w-3.5 h-3.5" />
                {cfg.label}
              </button>
            )
          })}
        </div>
      ) : (
        /* Selected flow — slim header (back + label + source), then body. */
        <div className="bg-system-gray-900/60 rounded-xl border border-system-gray-800 overflow-hidden">
          <div className="flex items-center justify-between gap-2 border-b border-system-gray-800 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setAction(null)}
                className="p-1 rounded hover:bg-system-gray-800 text-system-gray-400 hover:text-white transition flex-shrink-0"
                aria-label="Back to actions"
                title="Back to actions"
              >
                <ArrowLeftIcon className="w-3.5 h-3.5" />
              </button>
              <SparklesIcon className="w-3.5 h-3.5 text-brand flex-shrink-0" />
              <span className="text-xs font-bold text-white flex-shrink-0">
                {ACTIONS.find(a => a.key === action)?.label}
              </span>
              {asset && (
                <span className="text-[10px] text-system-gray-500 truncate">
                  {asset.sourceUrl || "Selected asset"}
                </span>
              )}
            </div>
            {asset && (
              <button
                onClick={onClose}
                className="p-1 rounded hover:bg-system-gray-800 text-system-gray-400 hover:text-white transition flex-shrink-0"
                aria-label="Clear source asset"
                title="Clear source asset"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Body — single-source actions need an image source before their
              form can render; until one is picked they show a drop zone. The
              slideshow builds its own timeline so it always renders. */}
          <div className="p-4 text-left">
            {(action === "image-to-image" ||
              action === "image-to-carousel" ||
              action === "image-to-video") &&
              (hasImageSource ? (
                <ImageToVideoPanel
                  mode={
                    action === "image-to-image"
                      ? "image"
                      : action === "image-to-carousel"
                        ? "carousel"
                        : "video"
                  }
                  asset={asset}
                  onSelectAsset={onSelectAsset}
                  onClearAsset={onClose}
                  stagedAssets={stagedAssets}
                  poolAssets={poolAssets}
                  onGenerated={onGenerated}
                  onAssetReady={onAssetReady}
                  onUseAsCarouselAd={
                    onUseAsCarouselAd &&
                    (assets => {
                      onUseAsCarouselAd(assets)
                      handleFlowDone()
                    })
                  }
                  onOpenSlideshow={assets => {
                    setSlideshowSeed(assets)
                    setAction("slideshow")
                  }}
                  onClose={handleFlowDone}
                />
              ) : (
                <SourceDropZone
                  actionLabel={
                    ACTIONS.find(a => a.key === action)?.label ?? "This action"
                  }
                  isVideoSource={isVideoSource}
                  candidates={sourceCandidates}
                  onSelectAsset={onSelectAsset}
                />
              ))}
            {action === "slideshow" && (
              <MultiAssetFlow
                seedAsset={slideshowSeed ? null : asset}
                initialAssets={
                  // Fall back to the whole pool when only a hero is staged:
                  // a feed-seeded session stages one card but pools every
                  // product image, and a one-slide timeline defeats the point
                  // of a multi-shot video.
                  slideshowSeed ??
                  (stagedAssets && stagedAssets.length > 1
                    ? stagedAssets
                    : (poolAssets ?? stagedAssets))
                }
                onGenerated={onGenerated}
                onClose={handleFlowDone}
              />
            )}
            {action === "brand-overlay" && (
              <BrandOverlayPanel
                asset={asset}
                candidates={overlayCandidates}
                onSelectAsset={onSelectAsset}
                onClearAsset={onClose}
                onGenerated={onGenerated}
                onClose={handleFlowDone}
              />
            )}
          </div>
        </div>
      )}
    </section>
  )
}

// ── Image → Video panel ──────────────────────────────────────────────────

function ImageToVideoPanel({
  mode,
  asset,
  onSelectAsset,
  onClearAsset,
  stagedAssets,
  poolAssets,
  onAssetReady,
  onUseAsCarouselAd,
  onOpenSlideshow,
  onClose,
}: PanelProps) {
  const isImageMode = mode === "image"
  const isCarouselMode = mode === "carousel"
  const isVideoMode = mode === "video"
  // ── Ad brief state ─────────────────────────────────────────────────────
  const [refs, setRefs] = useState<RefImage[]>([])
  const [background, setBackground] = useState<BackgroundMode>("auto")
  const [customBackground, setCustomBackground] = useState("")
  const [headline, setHeadline] = useState("")
  const [cta, setCta] = useState("")
  const [effects, setEffects] = useState<ReadonlySet<string>>(
    () => new Set(["cinematic"]),
  )
  const [motionNotes, setMotionNotes] = useState("")
  const [durationSeconds, setDurationSeconds] = useState<VeoDuration>("6")
  const [resolution, setResolution] = useState<VeoResolution>("1080p")

  const [composeDone, setComposeDone] = useState(0)

  const [cardCount, setCardCount] = useState<CarouselCardCount>(3)

  // Output aspect ratios — always asked, per mode: which placement ratios
  // the image flow composes, which ratio carousel cards use, and which
  // ratios the video flow animates.
  const [frameRatioChoice, setFrameRatioChoice] = useState<"all" | FrameRatio>(
    "all",
  )
  const [carouselRatio, setCarouselRatio] = useState<"1:1" | "4:5" | "9:16">(
    "1:1",
  )
  const [videoRatioChoice, setVideoRatioChoice] = useState<"both" | VeoRatio>(
    "both",
  )
  const selectedFrameRatios: FrameRatio[] =
    frameRatioChoice === "all" ? FRAME_RATIOS : [frameRatioChoice]
  const selectedVeoRatios =
    videoRatioChoice === "both"
      ? VEO_RATIOS
      : VEO_RATIOS.filter(v => v.ratio === videoRatioChoice)
  // Product-page lookup (URL → scraped images + copy) and everything
  // derived from it — see ./ai-action/hooks/useProductPage.
  const {
    productUrl,
    setProductUrl,
    isFetchingUrl,
    urlImages,
    urlTexts,
    urlProduct,
    handleFetchUrl,
    scrapedProductContext,
    headlineSuggestions,
  } = useProductPage()

  const [status, setStatus] = useState<"idle" | "composing" | "submitting">(
    "idle",
  )
  // Number of variants completed so far during the current run, surfaced
  // in the status pill as "Generating 1/2…" etc.
  const [progressDone, setProgressDone] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const cancelledRef = useRef(false)

  const isBusy = status !== "idle"

  // A brief change invalidates the composed frame — animating a stale frame
  // would silently ignore the user's edits.
  const refsKey = refs.map(r => `${r.asset.id}:${r.role}`).join(",")
  useEffect(() => {
    setComposedFrames({})
    setComposedCards([])
  }, [
    asset.id,
    frameRatioChoice,
    carouselRatio,
    videoRatioChoice,
    refsKey,
    background,
    customBackground,
    headline,
    cta,
    urlTexts,
    urlProduct,
  ])

  // Cleanup on unmount so a backgrounded poll doesn't outlive the dialog.
  useEffect(
    () => () => {
      cancelledRef.current = true
    },
    [],
  )

  // ── Merge images (refs) ────────────────────────────────────────────────

  const usedImageIds = new Set([asset.id, ...refs.map(r => r.asset.id)])
  // Staged slot assets that could still be merged in — shown as one-click
  // quick-add thumbs. Needs a library assetId (compose resolves ids
  // server-side) and can't be video/AVIF.
  // Auto-select merge references by attribute.
  //
  // Feed-seeded pools arrive pre-classified, so rather than making the user
  // hand-pick, take ONE image per distinct category — a packshot for product
  // accuracy, a lifestyle frame for scene, an on-model shot for fit, a close-up
  // for texture. One per category on purpose: three near-identical lifestyle
  // shots tell the model nothing extra and just crowd the reference budget.
  //
  // Runs once per hero, and never overrides a manual selection.
  const autoSelectedRef = useRef<string | null>(null)
  useEffect(() => {
    const heroId = mediaRef(asset)
    if (!heroId || autoSelectedRef.current === heroId) return
    if (refs.length > 0) return

    const pool = poolAssets ?? []
    const byCategory = new Map<ImageCategory, ScrapedImage>()
    for (const candidate of pool) {
      if (!mediaRef(candidate) || mediaRef(candidate) === heroId) continue
      if (candidate.type === "video" || isAvifImage(candidate)) continue
      if (!CATEGORY_PROMPT_LINES[candidate.category]) continue
      if (candidate.category === asset.category) continue
      if (!byCategory.has(candidate.category)) {
        byCategory.set(candidate.category, candidate)
      }
    }
    if (byCategory.size === 0) return

    // Order the shot types deliberately, then take one of each: lifestyle for
    // scene, on-model for fit, detail for texture, packshot for product
    // accuracy. Four is the full set the feed classifies.
    const SHOT_ORDER: ImageCategory[] = [
      ImageCategory.LIFESTYLE,
      ImageCategory.ON_BODY,
      ImageCategory.PRODUCT_DETAIL,
      ImageCategory.LAYDOWN,
      ImageCategory.FABRIC_DETAIL,
    ]

    autoSelectedRef.current = heroId
    setRefs(
      SHOT_ORDER.map(category => byCategory.get(category))
        .filter((a): a is ScrapedImage => Boolean(a))
        .slice(0, 4)
        .map(candidate => ({
          asset: candidate,
          role: CATEGORY_TO_ROLE[candidate.category] ?? ("product" as RefRole),
        })),
    )
  }, [asset.assetId, asset.url, asset.category, poolAssets, refs.length])

  const stagedCandidates = (stagedAssets ?? []).filter(
    a =>
      a.type !== "video" &&
      !isAvifImage(a) &&
      !!a.assetId &&
      !usedImageIds.has(a.id),
  )

  const addRef = (a: ScrapedImage, role: RefRole = "product") =>
    setRefs(prev =>
      prev.some(r => r.asset.id === a.id)
        ? prev
        : [...prev, { asset: a, role }],
    )
  const removeRef = (id: string) =>
    setRefs(prev => prev.filter(r => r.asset.id !== id))
  const setRefRole = (id: string, role: RefRole) =>
    setRefs(prev => prev.map(r => (r.asset.id === id ? { ...r, role } : r)))

  const refDroppable = useDroppable({
    id: AD_REF_DROP_ID,
    data: { kind: "ad-ref" },
  })
  useDndMonitor({
    onDragEnd(event: DragEndEvent) {
      const { active, over } = event
      if (!over || String(over.id) !== AD_REF_DROP_ID) return
      if (!String(active.id).startsWith(DRAWER_ITEM_PREFIX)) return
      const data = active.data.current as
        | { kind: "pool"; asset: ScrapedImage }
        | undefined
      const dropped = data?.asset
      if (!dropped) return
      if (dropped.type === "video") {
        toast.warning("Only images can be merged into the ad frame")
        return
      }
      if (isAvifImage(dropped)) {
        toast.warning("AVIF images can't go through the image pipeline")
        return
      }
      addRef(dropped)
    },
  })

  // Hero Product tile is droppable too — dropping a drawer image swaps the
  // flow's source product (the parent owns that state). If the new product
  // was sitting in the merge refs, drop it from there.
  const heroDroppable = useDroppable({
    id: HERO_DROP_ID,
    data: { kind: "ai-hero" },
  })
  useDndMonitor({
    onDragEnd(event: DragEndEvent) {
      const { active, over } = event
      if (!over || String(over.id) !== HERO_DROP_ID) return
      if (!String(active.id).startsWith(DRAWER_ITEM_PREFIX)) return
      const data = active.data.current as
        | { kind: "pool"; asset: ScrapedImage }
        | undefined
      const dropped = data?.asset
      if (!dropped || dropped.id === asset.id) return
      if (dropped.type === "video") {
        toast.warning("The product source must be an image")
        return
      }
      if (isAvifImage(dropped)) {
        toast.warning("AVIF images can't go through the image pipeline")
        return
      }
      removeRef(dropped.id)
      onSelectAsset?.(dropped)
    },
  })

  // Product facts carried in from the Mega Feed, when the session was seeded
  // from a product rather than an ad/post. Richer and cleaner than the scraped
  // digest below — the crawler and LLM extraction already ran — so it wins when
  // present, and no URL scrape round-trip is needed.
  const feedProductContext = useAppSelector(
    state => state.adForge.productContext,
  )

  // Standalone when a product is attached: the feed already carries the name,
  // category, one-liner, specs and price, so the prompt is built from that
  // alone and never consults the page scrape. The scraped digest is only for
  // ad/post-seeded sessions that have no product at all.
  const { data: brandKit } = useAdForgeGetBrandKitQuery()
  const brandContext = buildBrandContext(brandKit)

  const feedDigest = buildProductContextDigest(feedProductContext)
  const productContext = feedProductContext ? feedDigest : scrapedProductContext

  // One-click merge candidates: staged slot images + the product page's
  // scraped images, minus anything already in the ad.
  const quickAddCandidates = (() => {
    const seen = new Set<string>()
    const out: ScrapedImage[] = []
    for (const a of [...stagedCandidates, ...urlImages]) {
      if (usedImageIds.has(a.id) || seen.has(a.id)) continue
      seen.add(a.id)
      out.push(a)
    }
    return out
  })()

  // ── Prompt builders ────────────────────────────────────────────────────

  // Ordered image list for a compose call. Each entry pairs the asset id
  // with the role line that describes it in the prompt manifest — id order
  // and manifest numbering can never drift apart. Hero product shot always
  // comes first; `includeQuickAdd` additionally pulls in every remaining
  // staged/page image (the lucky flow).
  const buildComposeInputs = (includeQuickAdd: boolean) => {
    const inputs: { assetId: string; roleLine: string }[] = []
    const seen = new Set<string>()
    const push = (assetId: string | undefined, roleLine: string) => {
      if (!assetId || seen.has(assetId)) return
      seen.add(assetId)
      inputs.push({ assetId, roleLine })
    }
    push(mediaRef(asset), HERO_ROLE_LINE)
    for (const r of refs) {
      push(mediaRef(r.asset), describeRef(r.asset, ROLE_PROMPT_LINES[r.role]))
    }
    if (includeQuickAdd) {
      for (const a of quickAddCandidates) {
        push(mediaRef(a), describeRef(a, SUPPORTING_ROLE_LINE))
      }
    }
    // Cap the reference set so Imagen isn't drowned in inputs.
    return inputs.slice(0, 8)
  }

  // The images Veo actually sees, in the order it sees them: the hero goes
  // first (the server sends it as reference #1), then the role-tagged refs —
  // and the wider staged/page pool on top of those for the lucky flow. Capped
  // to the model's limit here rather than letting the server truncate
  // silently, so the panel can label exactly what will be used.
  const buildMotionInputs = (includeQuickAdd: boolean) => {
    const heroId = mediaRef(asset)
    const inputs = buildComposeInputs(includeQuickAdd)
    return [
      ...inputs.filter(i => i.assetId === heroId),
      ...inputs
        .filter(i => i.assetId !== heroId)
        .slice(0, MAX_MOTION_REFERENCES - 1),
    ]
  }

  const buildMotionReferences = (includeQuickAdd: boolean) =>
    buildMotionInputs(includeQuickAdd)
      .map(i => i.assetId)
      .filter(id => id !== mediaRef(asset))

  // Tell Veo what each reference depicts. Without this the extra shots are
  // just unlabelled pixels and the model has to guess whether an image is the
  // product, the scene or a style cue.
  const motionManifest = (includeQuickAdd: boolean) =>
    buildMotionManifest(buildMotionInputs(includeQuickAdd))

  const motionReferenceIds = isVideoMode ? buildMotionReferences(false) : []
  const usesMotionReferences = motionReferenceIds.length > 0
  // References the model will never see, because Veo's 3-image cap is already
  // spent. Surfaced in the images row rather than left to a hover tooltip.
  const droppedRefCount = isVideoMode
    ? refs.filter(r => !motionReferenceIds.includes(mediaRef(r.asset) ?? ""))
        .length
    : 0
  const effectiveDuration: VeoDuration = usesMotionReferences
    ? REFERENCE_MODE_DURATION
    : durationSeconds

  const buildComposePrompt = (
    inputs: { assetId: string; roleLine: string }[],
  ) => {
    // Creative-director base first (manifest + full art-direction
    // freedom), then every brief item the user actually filled in becomes
    // a REQUIREMENT stacked on top of it.
    const parts: string[] = [
      buildImageManifest(inputs),
      CREATIVE_DIRECTOR_PROMPT,
    ]
    const hasBgRef = refs.some(r => r.role === "background")
    switch (background) {
      case "original":
        parts.push(
          "Background requirement: keep the product's original background and setting.",
        )
        break
      case "white":
        parts.push(
          "Background requirement: place the product on a clean, pure white seamless studio background with a soft, realistic contact shadow.",
        )
        break
      case "reference":
        parts.push(
          hasBgRef
            ? "Background requirement: take the background and scene from the image(s) marked as a BACKGROUND reference and place the product naturally into that scene, matching perspective, lighting and shadows."
            : "Background requirement: use the most suitable non-product reference image as the background scene and place the product naturally into it, matching perspective, lighting and shadows.",
        )
        break
      case "custom":
        if (customBackground.trim()) {
          parts.push(`Background requirement: ${customBackground.trim()}.`)
        }
        break
      default:
        // "auto" — the base prompt already gives the model free rein.
        break
    }
    if (headline.trim()) {
      parts.push(
        `Headline requirement: add the headline text "${headline.trim()}" in bold, clean, modern advertising typography — spelled exactly like that, highly legible, placed so it never covers the product.`,
      )
    }
    if (cta.trim()) {
      parts.push(
        `Call-to-action requirement: add "${cta.trim()}" in a simple, clean button-like badge.`,
      )
    }
    if (headline.trim() || cta.trim()) {
      parts.push(
        "Text override: do NOT invent any headline of your own — the only text allowed is the requested headline/call-to-action and whatever is already on the product.",
      )
    }
    if (productContext) {
      parts.push(`Product context from its product page: ${productContext}`)
    }
    return parts.join(" ")
  }

  const buildMotionPrompt = () => {
    const phrases = EFFECT_OPTIONS.filter(e => effects.has(e.key)).map(
      e => e.phrase,
    )
    const parts: string[] = [
      phrases.length
        ? `Bring this product ad to life with ${phrases.join("; ")}.`
        : "Cinematic movement, bring this product to life gracefully.",
      "Keep the product exactly as it is — do not change its shape, colors, labels, or any text on it.",
    ]
    if (headline.trim() || cta.trim()) {
      parts.push(
        "Keep all on-image text perfectly static, crisp and legible — never warp, animate, or cover the text.",
      )
    }
    if (motionNotes.trim()) parts.push(motionNotes.trim())

    // Name the subject when the session was seeded from a feed product. The
    // rest of this prompt only ever says "this product", which leaves Veo to
    // infer the subject from pixels alone. Kept short on purpose — Veo prompts
    // are shot-focused, and a long product paragraph degrades the result.
    const subject = buildMotionProductContext(feedProductContext)
    if (subject) parts.unshift(`The product in frame is: ${subject}.`)

    return parts.join(" ")
  }

  // ── Generation pipeline ────────────────────────────────────────────────

  // Stage 2 (video mode) — submitting, polling and saving Veo clips lives in
  // its own hook; everything above builds what it runs with.
  const {
    pendingResults,
    setPendingResults,
    hasActiveItem,
    failedCount,
    allFailed,
    lastMotionPrompt,
    runBatch,
    handleSaveOne,
    handleRegenerateOne,
  } = useMotionPipeline({
    asset,
    selectedVeoRatios,
    durationSeconds,
    resolution,
    productContext,
    brandContext,
    cancelledRef,
    setStatus,
    setProgressDone,
    onAssetReady,
    buildFallbackPrompt: () => buildMotionPrompt(),
  })

  // Stage 1 — composing placement frames and carousel cards, plus the
  // per-card refine/regenerate/stop machinery.
  const {
    composedFrames,
    setComposedFrames,
    composedCards,
    setComposedCards,
    hasActiveCard,
    composeAllFrames,
    composeCarousel,
    refineCard,
    regenerateCard,
    stopCard,
  } = useComposePipeline({
    asset,
    cta,
    cardCount,
    carouselRatio,
    cancelledRef,
    setComposeDone,
    buildFirstCardRun: () => {
      const inputs = buildComposeInputs(false)
      return {
        prompt: `${buildComposePrompt(inputs)} ${carouselFirstCardSuffix(cardCount)}`,
        referenceIds: inputs.map(i => i.assetId),
      }
    },
  })

  const handleGenerate = async () => {
    if (isBusy || hasActiveItem || hasActiveCard) return
    if (!mediaRef(asset)) {
      const msg = "This image has no usable source; cannot generate."
      setError(msg)
      toast.error(msg)
      return
    }
    cancelledRef.current = false
    setError(null)
    setProgressDone(0)
    try {
      // Image mode IS the compose stage — always run it fresh (clicking
      // Generate again recomposes) and stop before the motion stage.
      if (isImageMode) {
        setStatus("composing")
        setComposedFrames({})
        const inputs = buildComposeInputs(false)
        const frames = await composeAllFrames(
          buildComposePrompt(inputs),
          inputs.map(i => i.assetId),
          selectedFrameRatios,
        )
        if (cancelledRef.current) return
        const count = Object.keys(frames).length
        toast.success(
          `${count} ad image${count === 1 ? "" : "s"} ready — add the ones you like to your pool.`,
        )
        return
      }
      // Carousel mode — compose card 1 from the brief, then derive the
      // remaining cards from it. Stops before the motion stage too.
      if (isCarouselMode) {
        setStatus("composing")
        const inputs = buildComposeInputs(false)
        const cards = await composeCarousel(
          `${buildComposePrompt(inputs)} ${carouselFirstCardSuffix(cardCount)}`,
          inputs.map(i => i.assetId),
        )
        if (cancelledRef.current) return
        toast.success(
          `${cards.length} carousel card${cards.length === 1 ? "" : "s"} ready — add the ones you like to your pool.`,
        )
        return
      }
      // Straight to Veo. generate-motion now takes the categorized references
      // itself (see generateOne), so there is no Imagen compose step — that
      // would flatten several shots into one frame and bill an extra
      // generation for the privilege.
      const heroId = mediaRef(asset) as string
      await runBatch(
        { "9:16": heroId, "16:9": heroId },
        [motionManifest(false), buildMotionPrompt()].filter(Boolean).join(" "),
        motionReferenceIds,
      )
    } catch (e) {
      if (!cancelledRef.current) {
        const msg = e instanceof Error ? e.message : "Generation failed"
        setError(msg)
        toast.error(msg)
        setPendingResults([])
      }
    } finally {
      setStatus("idle")
      setProgressDone(0)
    }
  }

  // "I'm feeling lucky" — hand EVERY available image (source, merges,
  // staged) to the model and let it art-direct frame + motion end to end.
  const handleLucky = async () => {
    if (isBusy || hasActiveItem || hasActiveCard) return
    if (!mediaRef(asset)) {
      const msg = "This image has no usable source; cannot generate."
      setError(msg)
      toast.error(msg)
      return
    }
    cancelledRef.current = false
    setError(null)
    setProgressDone(0)
    try {
      // Same prompt builder as Generate — the creative-director base IS
      // the default now, and any brief the user filled in stacks on top.
      // Lucky just widens the image pool to everything available.
      const heroId = mediaRef(asset) as string
      const inputs = buildComposeInputs(true)
      setStatus("composing")
      if (isCarouselMode) {
        const cards = await composeCarousel(
          `${buildComposePrompt(inputs)} ${carouselFirstCardSuffix(cardCount)}`,
          inputs.map(i => i.assetId),
        )
        if (cancelledRef.current) return
        toast.success(
          `${cards.length} carousel card${cards.length === 1 ? "" : "s"} ready — add the ones you like to your pool.`,
        )
        return
      }
      if (isImageMode) {
        setComposedFrames({})
        const frames = await composeAllFrames(
          buildComposePrompt(inputs),
          inputs.map(i => i.assetId),
          selectedFrameRatios,
        )
        if (cancelledRef.current) return
        const count = Object.keys(frames).length
        toast.success(
          `${count} ad image${count === 1 ? "" : "s"} ready — add the ones you like to your pool.`,
        )
        return
      }
      // Video mode — lucky widens the reference pool: staged and page images
      // the user never explicitly added become Veo references too, in manifest
      // order, so the model art-directs from everything available rather than
      // just the hand-picked refs.
      await runBatch(
        { "9:16": heroId, "16:9": heroId },
        [motionManifest(true), LUCKY_MOTION_PROMPT].filter(Boolean).join(" "),
        buildMotionReferences(true),
      )
    } catch (e) {
      if (!cancelledRef.current) {
        const msg = e instanceof Error ? e.message : "Generation failed"
        setError(msg)
        toast.error(msg)
        setPendingResults([])
      }
    } finally {
      setStatus("idle")
      setProgressDone(0)
    }
  }

  const statusLabel = (() => {
    switch (status) {
      case "composing":
        if (isCarouselMode) {
          return `Composing carousel cards — ${composeDone}/${cardCount}…`
        }
        return `Composing ad images — ${composeDone}/${selectedFrameRatios.length} ratio${selectedFrameRatios.length === 1 ? "" : "s"}…`
      case "submitting":
        return `Generating videos — ${progressDone}/${selectedVeoRatios.length} done, this can take a couple of minutes…`
      default:
        return null
    }
  })()

  const toggleEffect = (key: string) =>
    setEffects(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <div className="space-y-4">
      {/* Product page URL — pulls the page's DB-scraped images as merge
          candidates and reads its copy for headline/context. Compose modes
          only: the video flow animates the hero directly. */}
      {!isVideoMode && (
        <div className="flex gap-2 items-end">
          <label className="flex flex-col gap-1 flex-1">
            <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
              Product page URL (optional)
            </span>
            <input
              type="url"
              value={productUrl}
              onChange={e => setProductUrl(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  void handleFetchUrl()
                }
              }}
              disabled={isBusy || isFetchingUrl}
              placeholder="https://yourstore.com/products/…"
              className="input input-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50"
            />
          </label>
          <button
            onClick={handleFetchUrl}
            disabled={isBusy || isFetchingUrl || !productUrl.trim()}
            className="btn btn-sm bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white hover:border-brand/50 gap-1.5 disabled:opacity-50"
            title="Pull the page's scraped images and read its text for the ad brief"
          >
            {isFetchingUrl ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <GlobeAltIcon className="w-3.5 h-3.5" />
            )}
            Fetch
          </button>
        </div>
      )}
      {!isVideoMode && urlTexts.length > 0 && (
        <p className="text-[10px] text-system-gray-600 -mt-2">
          Page text loaded — it will guide the ad composition.
        </p>
      )}

      {/* Images — hero product + merge refs + drop slot + staged quick-add */}
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
            Images
          </span>
          <span className="text-[10px] text-system-gray-600">
            {isVideoMode
              ? "Drop an image on the Product tile to swap it — the video animates this shot directly."
              : "Drop an image on the Product tile to swap it. Merge more images — e.g. pull a background from one, product angles from another."}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap items-start">
          {/* Hero product shot — droppable: swaps the source product */}
          <div className="w-24">
            <div
              ref={heroDroppable.setNodeRef}
              className={cn(
                "relative aspect-square rounded-lg overflow-hidden ring-2 bg-system-gray-800 transition group",
                heroDroppable.isOver ? "ring-brand" : "ring-brand/60",
              )}
              title="Drop an image from the Selected Assets drawer to swap the product"
            >
              {asset.url && (
                <img
                  src={asset.thumbnailUrl || asset.url}
                  alt=""
                  className={cn(
                    "w-full h-full object-cover transition",
                    heroDroppable.isOver && "opacity-40",
                  )}
                />
              )}
              {onClearAsset && (
                <button
                  onClick={onClearAsset}
                  disabled={isBusy}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center bg-black/70 hover:bg-red-500 text-white transition opacity-0 group-hover:opacity-100"
                  aria-label="Remove product image"
                  title="Remove product image — pick another"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              )}
              {heroDroppable.isOver && (
                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-brand text-center px-1">
                  Drop to swap product
                </span>
              )}
              <span className="absolute bottom-0.5 left-0.5 bg-brand text-white text-[9px] font-bold px-1 py-0.5 rounded">
                Product
              </span>
            </div>
          </div>
          {/* Merge references, each with a role. In video mode only the first
              two reach Veo — the rest are dimmed rather than hidden so it is
              obvious which shots are actually informing the clip. */}
          {refs.map(r => {
            const droppedFromMotion =
              isVideoMode &&
              !motionReferenceIds.includes(mediaRef(r.asset) ?? "")
            return (
              <div key={r.asset.id} className="w-24 space-y-1">
                <div
                  className={cn(
                    "relative aspect-square rounded-lg overflow-hidden ring-1 ring-system-gray-700/50 bg-system-gray-800 group",
                    droppedFromMotion && "opacity-40",
                  )}
                  title={
                    droppedFromMotion
                      ? `Veo accepts ${MAX_MOTION_REFERENCES} images per clip and the product shot is one of them, so this reference is not sent to the model. Remove one of the references above it to use this one instead.`
                      : undefined
                  }
                >
                  <img
                    src={r.asset.thumbnailUrl || r.asset.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {droppedFromMotion && (
                    <span className="absolute bottom-0.5 left-0.5 right-0.5 bg-black/75 text-system-warning-500 text-[9px] font-bold px-1 py-0.5 rounded text-center leading-tight">
                      Over Veo&apos;s {MAX_MOTION_REFERENCES}-image limit
                    </span>
                  )}
                  <button
                    onClick={() => removeRef(r.asset.id)}
                    disabled={isBusy}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center bg-black/70 hover:bg-red-500 text-white transition opacity-0 group-hover:opacity-100"
                    aria-label="Remove image"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
                <select
                  value={r.role}
                  onChange={e =>
                    setRefRole(r.asset.id, e.target.value as RefRole)
                  }
                  disabled={isBusy}
                  className="select select-xs w-full bg-system-gray-800 border-system-gray-700 text-white text-[10px] focus:border-brand focus:outline-none disabled:opacity-50 p-0 pl-2"
                  title="What this image contributes to the ad"
                >
                  {(Object.keys(REF_ROLE_LABELS) as RefRole[]).map(role => (
                    <option key={role} value={role}>
                      {REF_ROLE_LABELS[role]}
                    </option>
                  ))}
                </select>
              </div>
            )
          })}
          {/* Reference slot — available in video mode too: Veo takes the
              references natively, and the role picked here becomes that
              image's line in the manifest sent with the prompt. */}
          <div
            ref={refDroppable.setNodeRef}
            className={cn(
              "w-24 aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-center px-1 transition",
              refDroppable.isOver
                ? "border-brand bg-brand/10 text-brand"
                : "border-system-gray-700/50 text-system-gray-500",
            )}
          >
            <PhotoIcon className="w-5 h-5 opacity-50 mb-1" />
            <span className="text-[9px] font-semibold">
              {refDroppable.isOver ? "Drop to add" : "Drop image"}
            </span>
          </div>
        </div>
        {quickAddCandidates.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <span className="text-[10px] text-system-gray-600">Quick add:</span>
            {quickAddCandidates.map(a => (
              <button
                key={a.id}
                onClick={() => addRef(a)}
                disabled={isBusy}
                className="w-8 h-8 rounded overflow-hidden ring-1 ring-system-gray-700/50 hover:ring-brand transition disabled:opacity-50"
                title="Add this staged image to the ad"
              >
                <img
                  src={a.thumbnailUrl || a.url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
        {/* State the cap where the images are, not only in a tooltip on the
            dimmed tiles — "Not sent" on its own reads as a bug. */}
        {isVideoMode && (
          <p
            className={cn(
              "text-[10px] mt-2",
              droppedRefCount > 0
                ? "text-system-warning-500"
                : "text-system-gray-500",
            )}
          >
            {droppedRefCount > 0
              ? `Veo takes ${MAX_MOTION_REFERENCES} images per clip and the product shot is one of them, so only the first ${MAX_MOTION_REFERENCES - 1} references below are sent — ${droppedRefCount} more ${droppedRefCount === 1 ? "is" : "are"} dimmed. Remove one of the first ${MAX_MOTION_REFERENCES - 1} to promote it.`
              : `Veo takes ${MAX_MOTION_REFERENCES} images per clip, including the product shot — up to ${MAX_MOTION_REFERENCES - 1} references.`}
          </p>
        )}
      </div>

      {/* Ad brief — background treatment + overlay text. Compose modes
          only: none of it applies to a direct hero animation. */}
      {!isVideoMode && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Background
              </span>
              <select
                value={background}
                onChange={e => setBackground(e.target.value as BackgroundMode)}
                disabled={isBusy}
                className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50 p-0 pl-2"
              >
                <option value="auto">Let AI decide</option>
                <option value="original">Keep original</option>
                <option value="white">White studio</option>
                <option value="reference">From reference image</option>
                <option value="custom">Custom…</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Headline text
              </span>
              <input
                type="text"
                value={headline}
                onChange={e => setHeadline(e.target.value)}
                disabled={isBusy}
                placeholder="e.g. 50% off this week"
                className="input input-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Call to action
              </span>
              <input
                type="text"
                value={cta}
                onChange={e => setCta(e.target.value)}
                disabled={isBusy}
                placeholder="e.g. Shop now"
                className="input input-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50"
              />
            </label>
          </div>
          {headlineSuggestions.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] text-system-gray-600">
                Headlines from the page:
              </span>
              {headlineSuggestions.map(t => {
                const text = t.content.trim()
                return (
                  <button
                    key={t.id}
                    onClick={() => setHeadline(text)}
                    disabled={isBusy}
                    className="px-2 py-0.5 rounded-full text-[10px] bg-system-gray-800 border border-system-gray-700 text-system-gray-300 hover:text-white hover:border-brand/50 transition disabled:opacity-50"
                    title="Use as headline"
                  >
                    {text.length > 40 ? `${text.slice(0, 40)}…` : text}
                  </button>
                )
              })}
            </div>
          )}
          {background === "custom" && (
            <input
              type="text"
              value={customBackground}
              onChange={e => setCustomBackground(e.target.value)}
              disabled={isBusy}
              placeholder="Describe the background scene — e.g. sunlit marble countertop with soft shadows"
              className="input input-sm w-full bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50"
            />
          )}
          {background === "reference" &&
            !refs.some(r => r.role === "background") && (
              <p className="text-[10px] text-system-warning-500">
                Add an image above and set its role to “Background” so the AI
                knows which scene to pull from.
              </p>
            )}
        </>
      )}

      {/* Output config — card count (carousel) + aspect ratio (always) */}
      <div className="flex gap-3 flex-wrap">
        {isCarouselMode && (
          <label className="flex flex-col gap-1 w-40">
            <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
              Carousel cards
            </span>
            <select
              value={cardCount}
              onChange={e =>
                setCardCount(Number(e.target.value) as CarouselCardCount)
              }
              disabled={isBusy}
              className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50 p-0 pl-2"
            >
              <option value={3}>3 cards</option>
              <option value={4}>4 cards</option>
              <option value={5}>5 cards</option>
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1 w-48">
          <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
            Output aspect ratio
          </span>
          {isCarouselMode ? (
            <select
              value={carouselRatio}
              onChange={e =>
                setCarouselRatio(e.target.value as "1:1" | "4:5" | "9:16")
              }
              disabled={isBusy || hasActiveCard}
              className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50 p-0 pl-2"
            >
              <option value="1:1">1:1 (square)</option>
              <option value="4:5">4:5 (portrait)</option>
              <option value="9:16">9:16 (stories)</option>
            </select>
          ) : isImageMode ? (
            <select
              value={frameRatioChoice}
              onChange={e =>
                setFrameRatioChoice(e.target.value as "all" | FrameRatio)
              }
              disabled={isBusy}
              className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50 p-0 pl-2"
            >
              <option value="all">All placements</option>
              <option value="1:1">1:1 (square)</option>
              <option value="4:5">4:5 (feed portrait)</option>
              <option value="9:16">9:16 (stories)</option>
              <option value="16:9">16:9 (landscape)</option>
            </select>
          ) : (
            <select
              value={videoRatioChoice}
              onChange={e =>
                setVideoRatioChoice(e.target.value as "both" | VeoRatio)
              }
              disabled={isBusy || hasActiveItem}
              className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50 p-0 pl-2"
            >
              <option value="both">9:16 + 16:9 (both)</option>
              <option value="9:16">9:16 (stories) only</option>
              <option value="16:9">16:9 (landscape) only</option>
            </select>
          )}
        </label>
      </div>

      {/* Effects & style chips — feed the motion prompt (video mode only) */}
      {isVideoMode && (
        <div>
          <span className="block text-[10px] uppercase tracking-wide text-system-gray-500 mb-1.5">
            Effects & style
          </span>
          <div className="flex flex-wrap gap-1.5">
            {EFFECT_OPTIONS.map(opt => {
              const isActive = effects.has(opt.key)
              return (
                <button
                  key={opt.key}
                  onClick={() => toggleEffect(opt.key)}
                  disabled={isBusy}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-[10px] font-semibold border transition disabled:opacity-50",
                    isActive
                      ? "bg-brand/15 text-brand border-brand/40"
                      : "bg-system-gray-800 text-system-gray-400 border-system-gray-700 hover:text-white",
                  )}
                  title={opt.phrase}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Motion notes + timing config (video mode only) */}
      {isVideoMode && (
        <div className="space-y-1.5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_130px_130px] gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Extra motion directions (optional)
              </span>
              <input
                type="text"
                value={motionNotes}
                onChange={e => setMotionNotes(e.target.value)}
                disabled={isBusy}
                placeholder="e.g. steam rising from the cup, camera orbits left"
                className="input input-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Duration
              </span>
              <select
                value={effectiveDuration}
                onChange={e =>
                  setDurationSeconds(e.target.value as VeoDuration)
                }
                disabled={isBusy || usesMotionReferences}
                title={
                  usesMotionReferences
                    ? "Reference images force an 8-second clip — remove them to pick a shorter length"
                    : undefined
                }
                className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50 p-0 pl-2"
              >
                <option value="4" disabled={usesMotionReferences}>
                  4 seconds
                </option>
                <option value="6" disabled={usesMotionReferences}>
                  6 seconds
                </option>
                <option value="8">8 seconds</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Resolution
              </span>
              <select
                value={resolution}
                onChange={e => setResolution(e.target.value as VeoResolution)}
                disabled={isBusy}
                className="select select-sm bg-system-gray-800 border-system-gray-700 text-white text-xs focus:border-brand focus:outline-none disabled:opacity-50 p-0 pl-2"
              >
                <option value="720p">720p</option>
                <option value="1080p">1080p</option>
                <option value="4k">4K</option>
              </select>
            </label>
          </div>
          {usesMotionReferences && (
            <p className="text-[10px] text-system-gray-500">
              Duration is locked to 8s: with {motionReferenceIds.length}{" "}
              reference
              {motionReferenceIds.length === 1 ? "" : "s"} attached this runs as
              reference-to-video, and that mode only produces 8-second clips.
              Remove every reference to choose 4s or 6s.
            </p>
          )}
        </div>
      )}

      {/* Composed ad frames — one per placement ratio. All are already in
          the library; "Add to pool" stages one for use as an image ad. Image
          mode only: the video flow hands its shots straight to Veo as
          references, so it never composes a frame. */}
      {isImageMode && Object.keys(composedFrames).length > 0 && (
        <div className="rounded-lg border border-system-gray-800 bg-system-gray-900/40 p-2 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
              Ad images — review &amp; add to pool
            </span>
            <button
              onClick={() => setComposedFrames({})}
              disabled={isBusy || hasActiveItem || hasActiveCard}
              className="btn btn-xs btn-ghost text-system-gray-400 hover:text-white disabled:opacity-50"
            >
              Discard all
            </button>
          </div>
          <div className="flex gap-2 flex-wrap items-end">
            {FRAME_RATIOS.map(ratio => {
              const frame = composedFrames[ratio]
              if (!frame) return null
              return (
                <div key={ratio} className="w-24 space-y-1">
                  <div
                    className="w-full rounded-md overflow-hidden bg-system-gray-800 ring-1 ring-system-gray-700/50"
                    style={{ aspectRatio: ratio.replace(":", "/") }}
                  >
                    <img
                      src={frame.thumbnailUrl || frame.url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[9px] font-bold text-white">
                      {ratio}
                    </span>
                    <button
                      onClick={() => {
                        void onAssetReady?.(frame)
                        toast.success(`${ratio} frame added to your pool`)
                      }}
                      disabled={isBusy}
                      className="btn btn-xs btn-ghost text-brand hover:bg-brand/10 disabled:opacity-50 px-1"
                      title="Stage this frame as an image ad asset"
                    >
                      Add to pool
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
          <p className="text-[10px] text-system-gray-500">
            One image per placement ratio. Generate again to recompose with the
            current brief.
          </p>
        </div>
      )}

      {/* Composed carousel cards — ordered set. Same review surface as
          the frames above: everything is already in the library, and
          "Add to pool" stages a card for the ad. */}
      <CarouselCardsPanel
        cards={composedCards}
        ratio={carouselRatio}
        isBusy={isBusy}
        hasActiveCard={hasActiveCard}
        quickAddCandidates={quickAddCandidates}
        onDiscardAll={() => setComposedCards([])}
        onStopCard={stopCard}
        onRegenerateCard={idx => void regenerateCard(idx)}
        onRefineCard={(idx, instruction, refImage) =>
          void refineCard(idx, instruction, refImage)
        }
        onAssetReady={onAssetReady}
        onUseAsCarouselAd={onUseAsCarouselAd}
        onOpenSlideshow={onOpenSlideshow}
      />

      {/* Status / error */}
      {(statusLabel || error) && (
        <div
          className={cn(
            "rounded-md px-3 py-2 text-[11px]",
            error
              ? "bg-system-error-500/10 text-system-error-500 border border-system-error-500/30"
              : "bg-brand/10 text-brand border border-brand/20",
          )}
        >
          {error ? error : statusLabel}
        </div>
      )}

      {/* Prominent retry CTA — Veo submissions/polls fail intermittently in
          prod (5xx, throttling, GCS hiccups). Surface a top-level "Try Again"
          when any variant is in the failed state so the user doesn't have to
          scroll past the cards to find the footer Regenerate button. */}
      {failedCount > 0 && !isBusy && !hasActiveItem && (
        <div className="rounded-md px-3 py-2 bg-system-error-500/10 text-system-error-500 border border-system-error-500/30 flex items-center justify-between gap-3">
          <span className="text-[11px]">
            {allFailed
              ? "All variants failed. Try again with the same or a tweaked brief."
              : `${failedCount} of ${pendingResults.length} variants failed. Try again, or regenerate just the failed ones below.`}
          </span>
          <button
            onClick={handleGenerate}
            className="btn btn-xs bg-system-error-500 text-white hover:bg-system-error-500/90 gap-1 flex-shrink-0"
          >
            <ArrowPathIcon className="w-3 h-3" />
            Try Again
          </button>
        </div>
      )}

      {/* Review — one card per ratio. Populated by runBatch; each card owns
          its own Save/Regenerate flow so a single failed ratio doesn't block
          the user from keeping the ones that worked. */}
      {pendingResults.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-wide text-system-gray-500">
            Review & save
          </div>
          <div className="grid grid-cols-2 gap-2 items-start">
            {pendingResults.map((r, idx) => (
              <PendingResultCard
                key={`${r.ratio}-${idx}`}
                result={r}
                defaultPrompt={lastMotionPrompt || buildMotionPrompt()}
                onSave={() => handleSaveOne(idx)}
                onRegenerate={promptOverride =>
                  handleRegenerateOne(idx, promptOverride)
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          onClick={onClose}
          disabled={isBusy || hasActiveItem || hasActiveCard}
          className="btn btn-sm btn-ghost text-system-gray-400 disabled:opacity-50"
        >
          {(
            isImageMode
              ? Object.keys(composedFrames).length > 0
              : isCarouselMode
                ? composedCards.length > 0
                : pendingResults.some(r => r.status === "saved")
          )
            ? "Done"
            : "Cancel"}
        </button>
        <button
          onClick={handleLucky}
          disabled={isBusy || hasActiveItem || hasActiveCard}
          className="btn btn-sm btn-outline border-brand/40 text-brand hover:bg-brand/10 hover:border-brand gap-1.5 disabled:opacity-50"
          title={
            isImageMode
              ? "Feed every image to the AI and let it art-direct the ad images end to end"
              : isCarouselMode
                ? "Feed every image to the AI and let it art-direct the carousel end to end"
                : "Feed every image to the AI and let it art-direct the ad video end to end"
          }
        >
          <BoltIcon className="w-3.5 h-3.5" />
          I'm feeling lucky
        </button>
        <button
          onClick={handleGenerate}
          disabled={isBusy || hasActiveItem || hasActiveCard}
          className="btn btn-sm bg-brand text-white hover:bg-brand/90 gap-1.5 disabled:opacity-50"
        >
          {isBusy ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            <SparklesIcon className="w-3.5 h-3.5" />
          )}
          {isBusy
            ? "Generating…"
            : isImageMode
              ? Object.keys(composedFrames).length > 0
                ? "Regenerate images"
                : "Generate ad images"
              : isCarouselMode
                ? composedCards.length > 0
                  ? "Regenerate cards"
                  : "Generate carousel"
                : pendingResults.length > 0
                  ? "Regenerate all"
                  : "Generate ad video"}
        </button>
      </div>
    </div>
  )
}
